import { createRoot } from "react-dom/client";
import { HelmetProvider } from "react-helmet-async";
import { initSentry } from "./lib/sentry";
import App from "./App.tsx";
import "./index.css";
import { versionWatcher } from "./lib/version-watcher";
import { getRunningVersion } from "./lib/version-source";

// Initialize Sentry before rendering (no-op if DSN not configured)
initSentry();

/**
 * Native (Capacitor) detection — runs before anything else so we can skip
 * web-only boot logic that would otherwise hang the WebView on Android/iOS.
 */
function isCapacitorNative(): boolean {
  try {
    // @ts-ignore — Capacitor global injected by the native bridge
    const cap = (window as any).Capacitor;
    return !!(cap && typeof cap.isNativePlatform === "function" && cap.isNativePlatform());
  } catch {
    return false;
  }
}

async function hideNativeSplash() {
  if (!isCapacitorNative()) return;
  try {
    const { SplashScreen } = await import("@capacitor/splash-screen");
    await SplashScreen.hide({ fadeOutDuration: 200 });
    console.log("✅ [Native] SplashScreen.hide() called");
  } catch (e) {
    console.warn("⚠️ [Native] SplashScreen.hide() failed:", e);
  }
}

/**
 * Inside Capacitor WebView the Service Worker can interfere with the
 * local asset bundle. Proactively unregister any leftover SW.
 */
async function unregisterServiceWorkersOnNative() {
  if (!isCapacitorNative()) return;
  try {
    if ("serviceWorker" in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map((r) => r.unregister()));
      if (regs.length) console.log(`✅ [Native] Unregistered ${regs.length} SW(s)`);
    }
    if ("caches" in window) {
      const names = await caches.keys();
      await Promise.all(names.map((n) => caches.delete(n)));
    }
  } catch (e) {
    console.warn("⚠️ [Native] SW cleanup failed:", e);
  }
}

function boot() {
  console.log("[Boot] main loaded", { version: getRunningVersion() });

  // Apple review fix (Guideline 2.1): on native iOS/Android a leftover SW from
  // a prior web install can serve a stale index.html/JS bundle inside the
  // WKWebView. Fire the cleanup in the background — React MUST mount
  // immediately and never wait on this promise (otherwise a hanging SW
  // unregister would reproduce the blank white screen we are trying to fix).
  if (isCapacitorNative()) {
    void unregisterServiceWorkersOnNative()
      .then(() => console.log("[Boot] Native SW cleanup completed"))
      .catch((e) => console.warn("[Boot] Native SW cleanup failed", e));
  }

  // Mount React synchronously. No awaits above this line.
  console.log("[Boot] Starting React mount");
  try {
    createRoot(document.getElementById("root")!).render(
      <HelmetProvider><App /></HelmetProvider>
    );
    console.log("[Boot] App mounted");
  } catch (e) {
    console.error("[Boot] render failed", e);
  }


  // Hide splashes right after mount.
  requestAnimationFrame(() => {
    setTimeout(() => {
      hideNativeSplash();
      const htmlSplash = document.getElementById("native-splash");
      if (htmlSplash) {
        htmlSplash.classList.add("fade-out");
        htmlSplash.style.pointerEvents = "none";
        setTimeout(() => htmlSplash.parentNode?.removeChild(htmlSplash), 300);
      }
    }, 100);
  });

  setTimeout(() => {
    hideNativeSplash();
    const htmlSplash = document.getElementById("native-splash");
    if (htmlSplash) htmlSplash.parentNode?.removeChild(htmlSplash);
  }, 4000);

  // Background, non-blocking maintenance.
  void (async () => {
    try {
      await registerKillSwitchSW();
    } catch (e) {
      console.warn("[Boot] SW kill-switch registration failed", e);
    }
    // Start the global version watcher. This is the ONLY auto-update entry
    // point inside React. Polls every 60s + visibilitychange/focus/online/
    // pageshow triggers. Respects exam/practice sessions and includes
    // anti-loop, anti-cache and Real-Exam guards.
    try {
      versionWatcher.start();
    } catch (e) {
      console.warn("[Boot] versionWatcher.start failed", e);
    }
  })();
}

/**
 * SW Kill-Switch registration (WEB ONLY). Unchanged from previous architecture.
 */
async function registerKillSwitchSW() {
  if (isCapacitorNative()) return;
  if (!("serviceWorker" in navigator)) return;

  try {
    if (window.self !== window.top) {
      const force = new URLSearchParams(window.location.search).has("install-killswitch");
      if (!force) return;
    }
  } catch {
    return;
  }

  const forceInstall = new URLSearchParams(window.location.search).has("install-killswitch");
  const existing = await navigator.serviceWorker.getRegistrations();

  // Issue #22: in the wild some browsers ended up with BOTH /sw.js and the
  // legacy /pwa-sw.js (or other stale scripts) registered at once. Each
  // kill-switch only unregisters itself when its own update fires, so the
  // duplicate could linger for a session. Proactively unregister anything
  // that is not the canonical /sw.js kill-switch so only one SW remains.
  const KILL_SWITCH_PATH = "/sw.js";
  const duplicates = existing.filter((reg) => {
    const worker = reg.active || reg.waiting || reg.installing;
    const scriptURL = worker?.scriptURL ?? "";
    if (!scriptURL) return false;
    try {
      return new URL(scriptURL).pathname !== KILL_SWITCH_PATH;
    } catch {
      return !scriptURL.endsWith(KILL_SWITCH_PATH);
    }
  });
  if (duplicates.length > 0) {
    await Promise.all(
      duplicates.map(async (reg) => {
        try {
          await reg.unregister();
          console.log("[SW Kill-Switch] removed duplicate SW", reg.scope);
        } catch (e) {
          console.warn("[SW Kill-Switch] failed to remove duplicate SW", reg.scope, e);
        }
      })
    );
  }

  const remaining = existing.filter((reg) => !duplicates.includes(reg));

  if (remaining.length > 0) {
    await Promise.all(
      remaining.map(async (reg) => {
        try {
          await reg.update();
        } catch (e) {
          console.warn("[SW Kill-Switch] registration.update() failed", reg.scope, e);
        }
      })
    );
  }

  if (remaining.length === 0 && !forceInstall) {
    return;
  }


  try {
    const reg = await navigator.serviceWorker.register("/sw.js", { scope: "/" });
    console.log("[SW Kill-Switch] registered", { scope: reg.scope });
  } catch (e) {
    console.warn("[SW Kill-Switch] register() failed", e);
  }
}

boot();
